import { useState, useEffect, useRef } from "react";
import { Sparkles, ArrowUp, X } from "lucide-react";

export default function AskResultPopover({ open, onClose, onSubmit, projectName, resultTitle }) {
  const [question, setQuestion] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuestion("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = question.trim();
    if (q.length < 2) return;
    onSubmit(q);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ask-result-popover-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="ask-result-popover"
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#E2E8F0]">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-[#0F1F3D] font-bold text-base">
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#0F1F3D] text-white">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              Ask AI about this article
            </div>
            {projectName && <div className="text-xs text-[#64748B] mt-1 truncate">Project: {projectName}</div>}
            {resultTitle && <div className="text-xs text-[#0F1F3D] font-medium mt-1 line-clamp-2">{resultTitle}</div>}
            <p className="text-xs text-[#94A3B8] mt-1">You’ll be redirected to Analysis — answer will use only this article.</p>
          </div>
          <button onClick={onClose} data-testid="ask-result-popover-close" className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-[#F1F5F9] shrink-0">
            <X className="h-5 w-5 text-[#64748B]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <textarea
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                handleSubmit(e);
              }
            }}
            placeholder="e.g. Why is this relevant to our project? What action should we take?"
            data-testid="ask-result-input"
            rows={3}
            maxLength={1000}
            className="w-full px-3 py-2.5 rounded-lg border border-[#CBD5E1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-[#94A3B8]">{question.length}/1000</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid="ask-result-cancel"
                className="px-4 py-2 rounded-lg border border-[#E2E8F0] text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={question.trim().length < 2}
                data-testid="ask-result-submit"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0F1F3D] text-white text-sm font-semibold hover:bg-[#172A4F] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUp className="h-4 w-4" /> Ask AI
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
